// HR Letter generation UI — shared by Recruitment (candidate letters) and the
// Employee record (employee letters + warnings). Generates a PDF, opens it, and
// shows the letter history. hrkit / navy-red-warm.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { HRC, fmtDate, Card, SectionTitle, Th, Td, Table, Btn, Stat, Field, input, Modal, Empty, Loading } from './hrkit';

export const LETTER_TYPES = [
  { key: 'intent_letter', label: 'Intent Letter', stage: 'candidate' },
  { key: 'offer_letter', label: 'Offer Letter', stage: 'candidate', offer: true },
  { key: 'joining_report', label: 'Joining Report', stage: 'employee' },
  { key: 'induction_letter', label: 'Induction Letter', stage: 'employee' },
  { key: 'appointment_letter', label: 'Appointment Letter', stage: 'employee', draft: true, manager: true },
  { key: 'confirmation_letter', label: 'Confirmation Letter', stage: 'employee', draft: true },
  { key: 'relieving_letter', label: 'Relieving Letter', stage: 'employee', draft: true },
  { key: 'warning_letter', label: 'Warning Letter', stage: 'employee', draft: true, compose: true },
  { key: 'termination_letter', label: 'Termination Letter', stage: 'employee', draft: true, compose: true },
];
const typeLabel = (k) => (LETTER_TYPES.find(t => t.key === k) || {}).label || k;

function DraftTag() {
  return <span style={{ fontSize: 9, fontWeight: 800, color: HRC.red, background: HRC.red + '14', borderRadius: 4, padding: '1px 5px', marginLeft: 6 }}>DRAFT — review before issue</span>;
}

// One generate flow. Simple types generate on click; compose/offer open a form.
function GenerateModal({ type, scope, onClose, onDone }) {
  const cfg = LETTER_TYPES.find(t => t.key === type) || {};
  const [compose, setCompose] = useState('');
  const [fields, setFields] = useState({ ctc: '', doj: '', reporting_time: '', reporting_manager: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [missing, setMissing] = useState([]);   // a refusal: each field the letter needs, and where HR fills it
  const setF = (k) => (e) => setFields(p => ({ ...p, [k]: e.target.value }));

  const submit = () => {
    if (cfg.compose && !compose.trim()) { setErr('Please enter the reason/details.'); return; }
    setBusy(true); setErr(''); setMissing([]);
    const body = { letter_type: type, ...scope };
    if (cfg.compose) body.compose_note = compose;
    if (cfg.offer) body.fields = fields;
    if (cfg.manager && fields.reporting_manager.trim()) body.fields = { reporting_manager: fields.reporting_manager.trim() };
    api.post('/hr/letters/generate', body)
      .then(r => { const fp = r.data?.data?.file_path; if (fp) window.open(fp, '_blank'); onDone(); })
      .catch(e => {
        const d = e.response?.data || {};
        if (d.code === 'LETTER_FIELDS_MISSING') setMissing(d.missing || []);
        else setErr(d.error || 'Generation failed');
      }).finally(() => setBusy(false));
  };

  return (
    <Modal title={`Generate — ${cfg.label}`} onClose={onClose} width={560}>
      {cfg.draft && <div style={{ background: HRC.red + '12', color: HRC.red, fontSize: 12, fontWeight: 600, padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>DRAFT template — HR/legal review required before issue.</div>}
      {cfg.compose && (
        <Field label="Reason / Details">
          <textarea style={{ ...input, height: 120, resize: 'vertical' }} value={compose} onChange={e => setCompose(e.target.value)}
            placeholder={type === 'warning_letter' ? 'Describe the conduct/lapse being warned…' : 'State the grounds for termination…'} />
        </Field>
      )}
      {cfg.offer && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Offered CTC (per annum)"><input style={input} value={fields.ctc} onChange={setF('ctc')} placeholder="e.g. 480000" /></Field>
          <Field label="Join by (date)"><input type="date" style={input} value={fields.doj} onChange={setF('doj')} /></Field>
          <Field label="Reporting Time"><input style={input} value={fields.reporting_time} onChange={setF('reporting_time')} placeholder="e.g. 9:30 AM" /></Field>
          <Field label="Reports To"><input style={input} value={fields.reporting_manager} onChange={setF('reporting_manager')} placeholder="e.g. Plant Head" /></Field>
        </div>
      )}
      {cfg.manager && (
        <Field label="Reports to (optional)">
          <input style={input} value={fields.reporting_manager} onChange={setF('reporting_manager')} maxLength={80} placeholder="e.g. Plant Head" />
          <div style={{ fontSize: 11, color: HRC.muted, marginTop: 4 }}>Left empty, the letter leaves out the "You will report to…" sentence.</div>
        </Field>
      )}
      {!cfg.compose && !cfg.offer && !cfg.manager && <div style={{ fontSize: 13, color: HRC.muted }}>This will generate the letter PDF on the company letterhead and log it to history.</div>}
      {missing.length > 0 && <Missing list={missing} />}
      {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 10 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={busy}>{busy ? 'Generating…' : '📄 Generate PDF'}</Btn>
      </div>
    </Modal>
  );
}

// The letter was not generated: each field it needs, and where HR fills it (31 Oct 2026 — never a blank line).
function Missing({ list, compact }) {
  if (compact) return <div style={{ fontSize: 11, color: HRC.red, marginTop: 4, maxWidth: 220 }}>Needs: {list.map(m => m.label).join(', ')}</div>;
  return (
    <div style={{ background: HRC.red + '12', borderRadius: 6, padding: '10px 12px', marginTop: 12, fontSize: 12 }}>
      <div style={{ color: HRC.red, fontWeight: 700, marginBottom: 6 }}>Not generated. Fill these in first, then generate it again:</div>
      {list.map(m => <div key={m.field} style={{ marginBottom: 3 }}><b>{m.label}</b>: {m.where}</div>)}
    </div>
  );
}

// What each letter still needs for this person, shown under its button before the form is opened.
function useReadiness(scopeKey, scopeVal) {
  const [ready, setReady] = useState({});
  useEffect(() => {
    if (!scopeVal) return;
    api.get('/hr/letters/readiness', { params: { [scopeKey]: scopeVal } }).then(r => setReady(r.data.data || {})).catch(() => setReady({}));
  }, [scopeKey, scopeVal]);
  return ready;
}

function History({ rows }) {
  if (!rows.length) return <Empty>No letters generated yet.</Empty>;
  return (
    <Table>
      <thead><tr><Th>Letter</Th><Th>Date</Th><Th>Issued By</Th><Th style={{ textAlign: 'right' }}></Th></tr></thead>
      <tbody>
        {rows.map(l => (
          <tr key={l.id}>
            <Td style={{ fontWeight: 600 }}>{typeLabel(l.letter_type)}{l.warning_seq ? ` (#${l.warning_seq})` : ''}</Td>
            <Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.created_at)}</Td>
            <Td style={{ fontSize: 12, color: HRC.muted }}>{l.issued_by_name || '—'}</Td>
            <Td style={{ textAlign: 'right' }}>{l.file_path ? <a href={l.file_path} target="_blank" rel="noreferrer"><Btn size="sm" variant="soft">Download</Btn></a> : '—'}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function useLetters(scopeKey, scopeVal) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    if (!scopeVal) return;
    setLoading(true);
    api.get('/hr/letters', { params: { [scopeKey]: scopeVal } }).then(r => setRows(r.data.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [scopeKey, scopeVal]);
  useEffect(() => { load(); }, [load]);
  return { rows, loading, load };
}

/* ── Company-wide letters audit log (HR tab) ── */
export function LettersLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (type) params.letter_type = type;
    if (q) params.q = q;
    api.get('/hr/letters/log', { params }).then(r => setRows(r.data.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [type, q]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const thisMonth = new Date().toISOString().slice(0, 7);
  const kpi = { total: rows.length, month: rows.filter(r => (r.created_at || '').slice(0, 7) === thisMonth).length, warnings: rows.filter(r => r.letter_type === 'warning_letter').length };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="Letters Issued" value={kpi.total} />
        <Stat label="This Month" value={kpi.month} accent={HRC.green} />
        <Stat label="Warnings" value={kpi.warnings} accent={HRC.red} />
      </div>
      <Card>
        <SectionTitle>Letters Issued — Audit Log</SectionTitle>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input placeholder="Search name / employee code…" value={q} onChange={e => setQ(e.target.value)} style={{ ...input, width: 'auto', minWidth: 240, flex: '1 1 240px' }} />
          <select value={type} onChange={e => setType(e.target.value)} style={{ ...input, width: 'auto', minWidth: 170 }}>
            <option value="">All letter types</option>
            {LETTER_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          {(type || q) && <Btn variant="ghost" size="sm" onClick={() => { setType(''); setQ(''); }}>Reset</Btn>}
        </div>
        {loading ? <Loading /> : rows.length === 0 ? <Empty>No letters match.</Empty> : (
          <Table>
            <thead>
              <tr><Th>Recipient</Th><Th>Letter</Th><Th>Date</Th><Th>Issued By</Th><Th style={{ textAlign: 'right' }}></Th></tr>
            </thead>
            <tbody>
              {rows.map(l => (
                <tr key={l.id}>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{l.recipient_name || '—'}</div>
                    <div style={{ fontSize: 11, color: HRC.muted }}>{l.employee_code ? `${l.employee_code} · ` : ''}{l.recipient_type}</div>
                  </Td>
                  <Td>{typeLabel(l.letter_type)}{l.warning_seq ? ` (#${l.warning_seq})` : ''}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.created_at)}</Td>
                  <Td style={{ fontSize: 12, color: HRC.muted }}>{l.issued_by_name || '—'}</Td>
                  <Td style={{ textAlign: 'right' }}>{l.file_path ? <a href={l.file_path} target="_blank" rel="noreferrer"><Btn size="sm" variant="soft">Download</Btn></a> : '—'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/* ── Candidate letters (Recruitment) ── */
export function CandidateLetters({ candidateId }) {
  const { rows, loading, load } = useLetters('candidate_id', candidateId);
  const ready = useReadiness('candidate_id', candidateId);
  const [gen, setGen] = useState(null);
  return (
    <div style={{ marginTop: 16 }}>
      <SectionTitle>Letters</SectionTitle>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {LETTER_TYPES.filter(t => t.stage === 'candidate').map(t => {
          const needs = ready[t.key] || [];
          return (
            <div key={t.key}>
              <Btn variant="soft" onClick={() => setGen(t.key)} disabled={needs.length > 0}>📄 {t.label}</Btn>
              {needs.length > 0 && <Missing list={needs} compact />}
            </div>
          );
        })}
      </div>
      {loading ? <Loading /> : <History rows={rows} />}
      {gen && <GenerateModal type={gen} scope={{ candidate_id: candidateId }} onClose={() => setGen(null)} onDone={() => { setGen(null); load(); }} />}
    </div>
  );
}

/* ── Employee letters + warnings (Employee record) ── */
export function EmployeeLetters({ employeeId }) {
  const { rows, loading, load } = useLetters('employee_id', employeeId);
  const ready = useReadiness('employee_id', employeeId);
  const [gen, setGen] = useState(null);
  const warnings = rows.filter(r => r.letter_type === 'warning_letter').sort((a, b) => (a.warning_seq || 0) - (b.warning_seq || 0));

  return (
    <div>
      <SectionTitle>Generate Letter</SectionTitle>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {LETTER_TYPES.filter(t => t.stage === 'employee').map(t => {
          const needs = ready[t.key] || [];
          return (
            <div key={t.key}>
              <button onClick={() => setGen(t.key)} disabled={needs.length > 0}
                title={needs.length ? needs.map(m => `${m.label}: ${m.where}`).join('\n') : ''}
                style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 7, border: `1px solid ${HRC.border}`,
                  background: HRC.violet, color: HRC.navy, fontWeight: 600, fontSize: 13,
                  cursor: needs.length ? 'not-allowed' : 'pointer', opacity: needs.length ? 0.55 : 1 }}>
                📄 {t.label}{t.draft && <DraftTag />}
              </button>
              {needs.length > 0 && <Missing list={needs} compact />}
            </div>
          );
        })}
      </div>

      <Card style={{ marginBottom: 16 }}>
        <SectionTitle right={<span style={{ background: warnings.length ? HRC.red : HRC.muted, color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 800 }}>Warnings: {warnings.length}</span>}>Disciplinary Warnings</SectionTitle>
        {warnings.length === 0 ? <div style={{ fontSize: 13, color: HRC.muted }}>No warnings on record.</div> : (
          <div>
            {warnings.map(w => (
              <div key={w.id} style={{ borderLeft: `3px solid ${HRC.red}`, paddingLeft: 10, marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700 }}>{['First', 'Second', 'Final'][Math.min((w.warning_seq || 1) - 1, 2)]} Warning <span style={{ color: HRC.muted, fontWeight: 400 }}>· {fmtDate(w.created_at)}</span></div>
                {w.compose_note && <div style={{ fontSize: 12, color: HRC.text, marginTop: 2 }}>{w.compose_note}</div>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <SectionTitle>Letter History</SectionTitle>
      {loading ? <Loading /> : <History rows={rows} />}
      {gen && <GenerateModal type={gen} scope={{ employee_id: employeeId }} onClose={() => setGen(null)} onDone={() => { setGen(null); load(); }} />}
    </div>
  );
}
